import { useDispatch } from 'react-redux'
import { Resume, SingleTemplateSettings } from '@rio/types'

import { useI18n } from 'builder/hooks/useI18n'
import { SPACING } from 'builder/modules/constants'
import { actions } from 'builder/modules/resumeEditor'
import { SelectValue } from 'builder/components/Select/types'
import { useUpdateTemplateSettings } from 'builder/components/DocumentCustomizationModal'
import { useTextCustomization } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/hooks/useTextCustomization'

import { SlideInput } from '../../SlideInput/SlideInput'
import { CustomSelect } from '../../CustomSelect/CustomSelect'
import { TEXT_TAB_TRANSLATION } from '../constants'
import * as Styles from './styles'

type Props = {
  resume: Resume
  supportsNewCustomization?: boolean
}

const normalize = (value: number) => Math.min(Math.max(value, SPACING.min), SPACING.max)

export const FontSelection = ({ resume, supportsNewCustomization }: Props) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const {
    primaryFonts,
    secondaryFonts,
    tertiaryFonts,
    isPrimaryFontCustomizationEnabled,
    isSecondaryFontCustomizationEnabled,
    isTertiaryFontCustomizationEnabled,
    selectedPrimaryFont,
    selectedSecondaryFont,
    selectedTertiaryFont,
    spacing,
    supportsSpacing,
  } = useTextCustomization(resume)
  const { updateTemplateSettings } = useUpdateTemplateSettings(resume)

  const handleSettingsFieldChange =
    (field: keyof SingleTemplateSettings) => (value: SelectValue) => {
      updateTemplateSettings({ settingsName: field, value: value as string })
    }

  const handleLineSpacing = (value: number) => {
    if (supportsNewCustomization) {
      updateTemplateSettings({
        settingsName: 'lineSpacing',
        value: value,
      })
    } else {
      dispatch(
        actions.updateSimpleField({
          name: 'spacing',
          value: normalize(value),
          debounce: true,
        }),
      )
    }
  }

  return (
    <Styles.Container>
      <Styles.FontSelectContainer>
        <CustomSelect
          label={i18n.t(`${TEXT_TAB_TRANSLATION}.primary_font.title`)}
          options={primaryFonts}
          selected={selectedPrimaryFont}
          disabled={!isPrimaryFontCustomizationEnabled}
          onSelect={handleSettingsFieldChange('primaryFont')}
          disabledTooltip={i18n.t(`${TEXT_TAB_TRANSLATION}.primary_font.disabled_tooltip`)}
        />
        {!!secondaryFonts.length && (
          <CustomSelect
            label={i18n.t(`${TEXT_TAB_TRANSLATION}.secondary_font.title`)}
            options={secondaryFonts}
            selected={selectedSecondaryFont}
            disabled={!isSecondaryFontCustomizationEnabled}
            onSelect={handleSettingsFieldChange('secondaryFont')}
            disabledTooltip={i18n.t(`${TEXT_TAB_TRANSLATION}.secondary_font.disabled_tooltip`)}
          />
        )}
        {!!tertiaryFonts.length && (
          <CustomSelect
            label={i18n.t(`${TEXT_TAB_TRANSLATION}.tertiary_font.title`)}
            options={tertiaryFonts}
            selected={selectedTertiaryFont}
            disabled={!isTertiaryFontCustomizationEnabled}
            onSelect={handleSettingsFieldChange('tertiaryFont')}
            disabledTooltip={i18n.t(`${TEXT_TAB_TRANSLATION}.tertiary_font.disabled_tooltip`)}
          />
        )}
      </Styles.FontSelectContainer>
      <Styles.LineHeightContainer>
        <SlideInput
          unit="%"
          withMarks
          min={-1}
          max={1}
          step={0.5}
          value={spacing}
          defaultValue={0}
          disabled={!supportsSpacing}
          onChange={handleLineSpacing}
          name={i18n.t(`${TEXT_TAB_TRANSLATION}.line_height.title`)}
          disabledTooltip={i18n.t(`${TEXT_TAB_TRANSLATION}.line_height.disabled_tooltip`)}
        />
      </Styles.LineHeightContainer>
    </Styles.Container>
  )
}
